import { Trash2 } from 'lucide-react'
import clsx from 'clsx'
import { InfoTooltip } from '@/components/InfoTooltip'
import type { DiffPromptEntry } from '@/types/diff'

export interface DiffPromptBlockProps {
  promptEntry: DiffPromptEntry
  highlightedAnchor: string | null
  /** Discards this prompt file's entire draft edit. Undefined in the
   * read-only historical compare mode (no draft to discard from). */
  onDiscard?: () => void
}

const STATUS_BADGE_CLASSES: Record<string, string> = {
  added: 'bg-success/20 text-success',
  removed: 'bg-danger/20 text-danger',
  modified: 'bg-warning/20 text-warning',
}

const STATUS_DESCRIPTIONS: Record<string, string> = {
  added: 'This prompt file is new in this draft.',
  removed: 'This prompt file will be deleted when published.',
  modified: 'This prompt file’s content has changed.',
}

// One block per prompt file with a real change - mirrors DiffFileBlock's
// own shell (header, status badge, Discard), but the body is a plain-text
// diff (prompt content isn't a JSON tree, so there's no changes[]/path
// structure to walk) - reuses the same red-strikethrough/green styling
// Design PRD 10's Resolved Prompt Preview already established for an
// Override resolution, applied here to "old" vs "new" instead.
export function DiffPromptBlock({ promptEntry, highlightedAnchor, onDiscard }: DiffPromptBlockProps) {
  const blockId = `prompt-${promptEntry.filename}`

  return (
    <section
      id={blockId}
      className={clsx(
        'flex flex-col gap-3 rounded-control border bg-surface p-4 transition-colors',
        highlightedAnchor === blockId ? 'border-accent' : 'border-border',
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h2 className="font-mono text-header font-semibold text-text-primary">{promptEntry.filename}</h2>
          <span
            className={clsx(
              'rounded-pill px-2 py-0.5 text-meta font-medium capitalize',
              STATUS_BADGE_CLASSES[promptEntry.status],
            )}
          >
            {promptEntry.status}
          </span>
          <InfoTooltip text={STATUS_DESCRIPTIONS[promptEntry.status]} />
        </div>
        {onDiscard && (
          <button
            type="button"
            onClick={onDiscard}
            className="flex items-center gap-1 text-meta font-medium text-text-secondary hover:text-danger"
          >
            <Trash2 size={14} strokeWidth={1.5} />
            Discard
          </button>
        )}
      </div>

      <div className="flex flex-col gap-2">
        {promptEntry.oldContent !== null && (
          <pre className="whitespace-pre-wrap break-words rounded-control border border-danger/40 bg-danger/10 px-3 py-2 font-mono text-meta text-text-secondary line-through">
            {promptEntry.oldContent}
          </pre>
        )}
        {promptEntry.newContent !== null && (
          <pre className="whitespace-pre-wrap break-words rounded-control border border-success/40 bg-success/10 px-3 py-2 font-mono text-meta text-text-primary">
            {promptEntry.newContent}
          </pre>
        )}
      </div>
    </section>
  )
}
